import type { KeyboardHandlerEvent } from '@typings/utils.ts';

type HandlerConfig = {
  stopPropagation?: boolean;
};

type RequiredKeySpec = {
  key: string;
  shift?: boolean;
};

/**
 * General routine for examining a keyboard event and if it matches the key(s) specified
 * it calls the specified handler function.
 *
 * @param  {(string[] | string)} keys single string or array of strings representing keys you wish to handle
 * @param  {() => } handlerFn the event handler you wish to trigger if key is satisfied
 * @param  {Object} config extra configuration options
 * @param  {boolean} [config.stopPropagation=false] if true, stopPropogation is called on the event
 * @returns {boolean} true if the key spec was met and the event was handled
 */
const handleKeyboardEvent = (
  keys: string[] | string,
  handlerFn: (event: KeyboardHandlerEvent) => void,
  config: HandlerConfig = {},
) => {
  keys = Array.isArray(keys) ? keys : [keys]; // if keys is single key, convert to array
  return (event: KeyboardHandlerEvent) => {
    if (keys.includes(event.key)) {
      event.preventDefault();
      if (config.stopPropagation) {
        event.stopPropagation();
      }
      handlerFn(event);
      return true;
    } else {
      return false;
    }
  };
};

/**
 * Serves as a keydown event handler to activate the specified handerFn function
 * when the user presses a key that indicates "select" - currently Enter key or spacebar.
 *
 * @param  {() => } handlerFn the event handler you wish to trigger if key is satisfied
 * @param  {Object} config extra configuration options
 * @param  {boolean} [config.stopPropagation=false] if true, stopPropogation is called on the event
 * @returns {boolean} true if the key spec was met and the event was handled
 */
const handleKeyboardSelect = (handlerFn: (event: KeyboardHandlerEvent) => void, config: HandlerConfig = {}) =>
  handleKeyboardEvent(['Enter', ' '], handlerFn, config);

/**
 * Can be used as a keydown event handler to activate the specified function
 * (and optionally prevent default action) when the user presses the escape key.
 *
 * @param  {() => } handlerFn the event handler you wish to trigger if key is satisfied
 * @param  {Object} config extra configuration options
 * @param  {boolean} [config.stopPropagation=false] if true, stopPropogation is called on the event
 * @returns {boolean} true if the key spec was met and the event was handled
 */
const handleKeyboardEscape = (handlerFn: (event: KeyboardHandlerEvent) => void, config: HandlerConfig = {}) =>
  handleKeyboardEvent('Escape', handlerFn, config);

/**
 * Returns true if the keyboard event represents a "select" action
 * (Enter or Space), matching the keys used by handleKeyboardSelect.
 *
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
const isSelectKey = (e: KeyboardHandlerEvent) => e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar';

/**
 * Returns true if the keyboard event represents a printable character
 * with no modifier keys (Ctrl/Meta/Alt). Useful for intercepting typing
 * on non-input elements and redirecting focus to an input.
 *
 * @param {KeyboardEvent} e
 * @returns {boolean}
 */
const isPrintableKey = (e: KeyboardHandlerEvent) => e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey;

const callIfPressed =
  (requiredKeys: RequiredKeySpec, cb: (event: KeyboardHandlerEvent) => void) => (e: KeyboardHandlerEvent) => {
    const isPressed = e.key === requiredKeys.key || e.code === requiredKeys.key; // difference between key and code: https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
    const shiftIsPressedIfRequired = (requiredKeys.shift && e.shiftKey) || (!requiredKeys.shift && !e.shiftKey);

    if (isPressed && shiftIsPressedIfRequired) {
      cb(e);
    }
  };

// Pass an element or the document to bind to it. An id only finds the first match.
type KeyListenerTarget = string | HTMLElement | Document | null;

const resolveTarget = (target?: KeyListenerTarget) => {
  if (target === undefined) {
    return document.body;
  }
  return typeof target === 'string' ? document.getElementById(target) : target;
};

const listenToKey =
  (key: string, { shift = false }: { shift?: boolean } = {}) =>
  (cb: (event: KeyboardHandlerEvent) => void, target?: KeyListenerTarget) => {
    const element = resolveTarget(target);
    const listener = callIfPressed({ key, shift }, cb);
    const wrappedListener: EventListener = event => listener(event as unknown as KeyboardHandlerEvent);
    element?.addEventListener('keydown', wrappedListener);
    return () => element?.removeEventListener('keydown', wrappedListener);
  };

const listenToTab = listenToKey('Tab');
const listenToShiftTab = listenToKey('Tab', { shift: true });
const listenToEscape = listenToKey('Escape');

export {
  callIfPressed,
  handleKeyboardEscape,
  handleKeyboardEvent,
  handleKeyboardSelect,
  isPrintableKey,
  isSelectKey,
  listenToEscape,
  listenToKey,
  listenToShiftTab,
  listenToTab,
};
